// Libs
import clsx from "clsx";
import { isNil } from "lodash";
import moment from "moment";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import * as locales from "react-date-range/dist/locale";

// Utils
import { formatDateShort } from "src/design-system/core/formatters";
import { getCSSVariable, patchFormEventValue } from "src/design-system/core/utils";
import { getCalendarLocale, getLocale } from "src/design-system/i18n";
import type { RangeType } from "src/types/enum/RangeType";

// Components
import {
    DateRange as RDRDateRange,
    DateRangePicker as RDRDateRangePicker,
    type DateRangePickerProps as RDRDateRangePickerProps,
    type DateRangeProps as RDRDateRangeProps,
    type Range,
    type StaticRange,
} from "react-date-range";
import { Dropdown, type DropdownProps } from "../modals";
import { FieldHoc } from "./Field";
import { Input, type InputProps } from "./Input";

const DEFAULT_VALUE: Range = {
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
};

interface RangeWithId extends Range {
    id?: RangeType;
}

function findCorrectRange(
    options: StaticRange[],
    { startDate, endDate, key }: { startDate?: string; endDate?: string; key?: RangeType },
    defaultRange?: Range | null
): Range | null {
    if (startDate === undefined && endDate === undefined) {
        return {
            startDate: defaultRange.startDate,
            endDate: defaultRange.endDate,
            key: defaultRange?.key,
        };
    }

    const ranges = options.map((e) => e.range() as RangeWithId);
    const correctRangeList = ranges.filter(
        (r) =>
            moment(r.startDate).isSame(moment(startDate), "day") &&
            moment(r.endDate).isSame(moment(endDate), "day")
    );

    if (!correctRangeList || correctRangeList.length === 0) {
        return {
            startDate: moment(startDate).toDate(),
            endDate: moment(endDate).toDate(),
            key: "selection",
        };
    }

    if (correctRangeList.length === 1 || isNil(key)) {
        const correctRange = correctRangeList[0];
        return {
            startDate: moment(startDate).toDate(),
            endDate: moment(endDate).toDate(),
            // TODO : hack because time range are hardcoded in Flynt with ids instead of keys
            key: (correctRange as any).id ?? correctRange.key,
        };
    }

    const correctRange = correctRangeList.find((r) => r.id === key);
    return {
        startDate: moment(startDate).toDate(),
        endDate: moment(endDate).toDate(),
        // TODO : hack because time range are hardcoded in Flynt with ids instead of keys
        key: (correctRange as any).id ?? correctRange.key,
    };
}

export interface DateRangePickerProps extends Omit<InputProps, "value" | "onChange"> {
    /**
     * Additional className
     */
    className?: string;
    /**
     * Additional props for range picker
     */
    dateRangePickerProps?: RDRDateRangePickerProps | RDRDateRangeProps;
    /**
     * Default value (today)
     */
    defaultValue?: Range;
    /**
     * If date is disabled
     */
    disableFuture?: (date: Date) => boolean;
    /**
     * Additional props for Dropdown
     */
    dropdownProps?: DropdownProps;
    /**
     * Additional props for Dropdown
     */
    forceOpen?: boolean;
    /**
     * Input identifier
     */
    id: string;
    /**
     * Input name
     */
    name: string;
    /**
     * Change handler
     */
    onChange: (event: React.ChangeEvent<HTMLInputElement>, key: Range["key"]) => void;
    /**
     * Ranges to show as options
     */
    options?: StaticRange[];
    /**
     * Current value
     */
    value?: {
        startDate?: string;
        endDate?: string;
        customLabel?: string;
        customLabelWithDates?: boolean;
    };
    /**
     * If is displayed with static ranges
     */
    withStaticRanges?: boolean;
    /**
     * If end date is today, change it to now
     */
    useNowEndDate?: boolean;
}

const isEndDateTodayChangeDateToNow = (date: Date, useNowEndDate: boolean) => {
    if (useNowEndDate === false) {
        return date;
    }
    if (moment(date).isSame(moment(), "day")) {
        return moment().toDate();
    }
    return date;
};

export function DateRangePicker({
    className,
    dateRangePickerProps,
    defaultValue = DEFAULT_VALUE,
    disableFuture,
    dropdownProps,
    endAdornment,
    id,
    name,
    onChange,
    options = [],
    value = { customLabelWithDates: false },
    withStaticRanges = false,
    forceOpen = false,
    useNowEndDate = false,
    ...otherProps
}: DateRangePickerProps): JSX.Element {
    const rootClassName = clsx("ds-c-date-range-picker", className);

    const inputRef = useRef<HTMLInputElement>(null);
    const inputContainerRef = useRef<HTMLInputElement>(null);
    const [open, setOpen] = useState(false);
    const [state, setState] = useState<RangeWithId>(findCorrectRange(options, value, defaultValue));

    const locale = getLocale();
    const calendarLocale = getCalendarLocale(locale);

    useEffect(() => {
        if (forceOpen) {
            setOpen(true);
        }
    }, [forceOpen]);

    useEffect(() => {
        const correctRange = findCorrectRange(options, value, defaultValue);
        correctRange.endDate = isEndDateTodayChangeDateToNow(correctRange.endDate, useNowEndDate);
        if (state.key !== correctRange.key) {
            setState(correctRange);
        }
        if (
            state.key === "selection" &&
            (!moment(state.startDate).isSame(value.startDate, "day") ||
                !moment(state.endDate).isSame(value.endDate, "day"))
        ) {
            setState(correctRange);
        }
    }, [value.startDate, value.endDate]);

    const handleClick = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        if (onChange) {
            const dates = {
                startDate: state.startDate.toISOString(),
                endDate: state.endDate.toISOString(),
                key: state?.id,
            };
            const newEvent = patchFormEventValue(
                {} as React.SyntheticEvent<HTMLInputElement>,
                inputRef.current,
                dates
            );
            onChange(
                newEvent as React.ChangeEvent<HTMLInputElement>,
                findCorrectRange(options, dates).key
            );
            inputRef.current?.focus();
        }
    };

    const color = getCSSVariable("--ds-color--primary-500");

    const child: React.ReactNode =
        options.length > 0 || withStaticRanges ? (
            <RDRDateRangePicker
                className="ds-u-typography--body"
                direction="horizontal"
                disabledDay={(date) => (disableFuture ? disableFuture(date) : false)}
                inputRanges={[]}
                locale={locales[calendarLocale]}
                months={2}
                onChange={(item) => {
                    const [key] = Object.keys(item);
                    item[key].endDate = isEndDateTodayChangeDateToNow(
                        item[key].endDate,
                        useNowEndDate
                    );
                    setState(item[key]);
                }}
                rangeColors={[color]}
                ranges={[state]}
                staticRanges={options.length > 1 ? options : undefined}
                weekStartsOn={1}
                {...dateRangePickerProps}
            />
        ) : (
            <RDRDateRange
                className="ds-u-typography--body"
                direction="horizontal"
                disabledDay={(date) => (disableFuture ? disableFuture(date) : false)}
                locale={locales[calendarLocale]}
                months={2}
                onChange={(item) => {
                    setState(item.selection);
                }}
                rangeColors={[color]}
                ranges={[state]}
                weekStartsOn={1}
                {...dateRangePickerProps}
            />
        );

    const startDateLabel = value.startDate ? formatDateShort(value.startDate) : "";
    const endDateLabel = value.endDate ? formatDateShort(value.endDate) : "";
    const dateLabel = `${startDateLabel}${startDateLabel !== endDateLabel ? ` - ${endDateLabel}` : ""}`;

    const label = value.customLabel
        ? `${value.customLabel}${value.customLabelWithDates ? ` (${dateLabel})` : ""}`
        : dateLabel;

    return (
        <div className={rootClassName}>
            <Input
                {...otherProps}
                endAdornment={endAdornment ?? "calendar-today"}
                focused={open}
                id={id}
                name={name}
                onClick={handleClick}
                ref={inputRef}
                containerRef={inputContainerRef}
                type="date"
                value={label}
            />
            <Dropdown
                {...dropdownProps}
                anchorEl={inputContainerRef}
                onClose={handleClose}
                open={open}
            >
                {child}
            </Dropdown>
        </div>
    );
}

export const DateRangePickerField = FieldHoc(DateRangePicker, "DateRangePicker");
